'use client';
/**
 * 自定义 React Hook，使用 IntersectionObserver 检测元素是否在视口中。
 * 该 Hook 提供一个 callback ref 用于附加到目标元素，以及一个布尔值指示可见性。
 * 元素更换（如选中卡切换、内容晚渲染）时自动重绑 observer。
 */

import { useCallback, useEffect, useState } from 'react';

/** 默认可见比例阈值 */
const DEFAULT_THRESHOLD = 0.2;

/**
 * 用于观察元素在视口中可见性的 Hook。
 * @template T - 要观察的 HTMLElement 类型（例如 HTMLDivElement）。
 * @param threshold - 可见比例阈值，默认 0.2
 * @param enabled - 为 false 时不观察且 inView 恒为 false；用于等内容就绪（元素高度稳定）后再开始判定，
 * 避免对加载态的矮元素提前得出「已进视口」的结论
 * @returns 一个对象，包含：
 * - ref: 要附加到要观察的元素的 callback ref（可挂 JSX，也可透传给子组件）。
 * - inView: 一个布尔值，指示元素可见比例是否达到阈值。
 */
export function useInView<T extends HTMLElement>(threshold = DEFAULT_THRESHOLD, enabled = true) {
    // 用 state 持有元素而非 ref.current：元素更换时驱动下方 effect 重绑 observer
    const [element, setElement] = useState<T | null>(null);
    const [inView, setInView] = useState(false);

    // 稳定的 callback ref，避免每次渲染触发解绑/重挂
    const ref = useCallback((el: T | null) => {
        setElement(el);
    }, []);

    useEffect(() => {
        if (!enabled || !element) {
            setInView(false);
            return () => {};
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                // 相交比例达到阈值才算 inView；只判断 isIntersecting 会在元素刚露出 1 像素
                // （isIntersecting 翻转）时即置 true，threshold 形同虚设。
                setInView(entry.isIntersecting && entry.intersectionRatio >= threshold);
            },
            { threshold }
        );

        // 开始观察元素
        observer.observe(element);

        // 清理函数以停止观察元素
        return () => {
            observer.unobserve(element);
        };
    }, [element, threshold, enabled]);

    return { ref, inView };
}
